import { useTexte } from '../../texte'
import { rueckfrage } from '../../components/Rueckfrage'

import { nochmal, verwerfen, type OutboxEntry } from '../../lib/outbox'
import { useWartendeBilder } from '../../hooks/useWartendeBilder'
import { bubbleTime } from '../../lib/format'
import { trennerTag } from '../../lib/ortszeit'
import { KalenderIcon } from '../../components/Icons'

/**
 * Eine Nachricht, die noch auf Netz wartet.
 *
 * Sie steht dort, wo sie hingehört: unten im Verlauf, an der Stelle, an der
 * sie gleich stehen wird. Blass, mit „wartet auf Netz" statt eines Hakens —
 * ein Haken behauptete eine Zustellung, und genau die gibt es hier noch nicht.
 *
 * Zwei Knöpfe, und beide sind nötig: **Verwerfen**, weil der Text dem gehört,
 * der ihn geschrieben hat, und **Nochmal**, wenn es nicht von selbst geht.
 * Ohne den zweiten bliebe ein Eintrag für immer liegen.
 *
 * **„Wenn der Server abgelehnt hat" stand hier bis 1.37.0, und das war zu
 * eng.** Es gibt einen zweiten Fall, in dem nichts mehr von selbst passiert:
 * Netz ist da, und der Eintrag geht trotzdem nicht hinaus. Der Grund steht
 * unten an `haengt`.
 */
/**
 * What state a waiting entry is in -- the one rule, read by the bubble and
 * by the waiting weather card (#98). `haengt` is explained at `Wartend`.
 */
function wartetZustand(eintrag: OutboxEntry): 'absage' | 'haengt' | 'netz' {
  if (eintrag.problem !== undefined) return 'absage'
  return eintrag.versuche >= 2 ? 'haengt' : 'netz'
}

/** The marker next to the time: the state as an attribute, the sentence for the eye. */
export function Wartemarke({ eintrag }: { eintrag: OutboxEntry }) {
  const t = useTexte()
  const zustand = wartetZustand(eintrag)
  return (
    <span className="bubble__wartet-marke" data-wartet={zustand}>
      {zustand === 'absage' ? t.chat.nichtAngenommen : zustand === 'haengt' ? t.chat.gehtNichtRaus : t.chat.wartet}
    </span>
  )
}

/** The two buttons under a waiting entry: "Nochmal" where nothing happens by itself, "Verwerfen" always. */
export function Warteknoepfe({ eintrag }: { eintrag: OutboxEntry }) {
  const t = useTexte()
  const zustand = wartetZustand(eintrag)
  return (
    <div className="wartet-knoepfe">
      {zustand !== 'netz' && (
        <button type="button" data-wartet-knopf="nochmal" onClick={() => void nochmal(eintrag.key)}>
          {t.chat.nochmal}
        </button>
      )}
      <button
        type="button"
        data-wartet-knopf="verwerfen"
        onClick={async () => {
          if (await rueckfrage(t.chat.wartendeVerwerfen, t.allgemein.verwerfen)) {
            void verwerfen(eintrag.key)
          }
        }}
      >
        {t.allgemein.verwerfen}
      </button>
    </div>
  )
}

export function Wartend({ eintrag }: { eintrag: OutboxEntry }) {
  const t = useTexte()
  const bilder = useWartendeBilder(eintrag)
  // The voice message that waits with the post (1.80.0): its length, so that
  // the bubble says what goes out with it.
  const tonDauer = eintrag.ton === undefined ? null : (() => {
    const sekunden = Math.round(eintrag.ton.dauerMs / 1000)
    return `${Math.floor(sekunden / 60)}:${String(sekunden % 60).padStart(2, '0')}`
  })()

  /**
   * **„Wartet auf Netz" nur, solange das auch stimmt.**
   *
   * Aus dem Betrieb (1.37.0): „es steht nach wie vor wartet auf Netz, obwohl
   * Netz da ist." Der Eintrag hatte es mehrfach vergeblich versucht, und die
   * Blase behauptete trotzdem weiter, es liege am Netz.
   *
   * Warum sie das kann, steht in `api/client.ts`: ein Upload, der ohne
   * Antwort abbricht, kommt als `ApiError(0)` zurück -- und Status 0 heißt in
   * dieser Anwendung „kein Netz". Ein Foto, das aus einem anderen Grund nicht
   * hinausgeht, sieht damit aus wie eine fehlende Leitung. Der Korb versucht
   * es weiter, und das soll er auch; falsch war nur der Satz daneben.
   *
   * **Gezählt wird, nicht gefragt.** Der naheliegende Weg wäre `useOnline()`
   * -- und der ist falsch, denn derselbe Fehlschlag meldet der Anwendung
   * gerade „kein Netz" (`netzMelden(false)` am `onerror`). Die Blase stünde
   * dann wieder auf „wartet". Gemessen im Prüfstand, und zwar als roter Lauf
   * mit der Marke `netz`.
   *
   * `senden()` läuft **nur mit Netz** (`if (laeuft || !netzStand()) return`).
   * Ein **zweiter** vergeblicher Anlauf heißt deshalb zwangsläufig: die
   * Anwendung hatte zwischendurch wieder Netz und es ging trotzdem nicht. Ein
   * einzelner sagt das nicht -- so sieht eine Leitung aus, die mitten im Bild
   * reißt.
   */
  return (
    <div className="bubble-row bubble-row--own">
      <div className="bubble bubble--wartet">
        {/* **Dieselbe obere Zeile wie an einer gesendeten Blase** — links
            steht hier niemand (die eigene Nachricht nennt keinen Absender),
            und der leere Platz schiebt die Angabe nach rechts. Ohne die
            Zeile stünde die Uhrzeit seit dem Wegfall des Umflusses mitten
            im Text. */}
        <span className="bubble__kopfzeile">
          <span className="bubble__wer" />
          <span className="bubble__meta">
            {bubbleTime(eintrag.angelegt)}
            {/* Das Merkmal trägt den Zustand, nicht der Satz -- ein Lauf, der
                den Wortlaut festhält, wird bei der nächsten Umformulierung rot
                und hat nichts gefunden. Seit 1.54.0 dieselbe Marke wie an
                der wartenden Wetterkarte. */}
            <Wartemarke eintrag={eintrag} />
          </span>
        </span>

        {bilder.length > 0 && (
          <div className="wartet-bilder">
            {bilder.map((url) => (
              <img key={url} src={url} alt="" />
            ))}
          </div>
        )}

        {tonDauer !== null && (
          <p className="wartet-ton" data-wartet-ton="">{t.chat.tonWartet(tonDauer)}</p>
        )}

        {/* The day separator that waits with the post (#113). */}
        {eintrag.tagestrenner !== undefined && (
          <p className="wartet-trenner" data-wartet-trenner={eintrag.tagestrenner.day}>
            <KalenderIcon size={16} />
            <span>
              {t.chat.tagestrenner.wartet(
                t.chat.tagestrenner.zaehler(eintrag.tagestrenner.word, eintrag.tagestrenner.number),
                trennerTag(eintrag.tagestrenner.day).datum,
              )}
            </span>
          </p>
        )}

        {eintrag.body}

        {eintrag.problem !== undefined && (
          <p className="wartet-problem">{eintrag.problem}</p>
        )}

        <Warteknoepfe eintrag={eintrag} />
      </div>
    </div>
  )
}
